import React, { useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import {
  CheckCircle, ChevronDown, ChevronUp, FileText,
  Loader2, Mail, MessageSquare, Plus, RefreshCw, Send, Trash2, Users, XCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { ExtractObligationsButton } from "./ExtractObligationsButton";

const NOTICE_TYPES = [
  { value: "breach", label: "Breach of Contract" },
  { value: "delay", label: "Delay Notice" },
  { value: "quality_failure", label: "Quality Failure" },
  { value: "cure", label: "Cure Notice" },
  { value: "warning", label: "Warning Notice" },
  { value: "termination", label: "Termination Notice" },
  { value: "general", label: "General Notice" },
];

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700" },
  in_review: { label: "In Review", color: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700" },
  sent: { label: "Sent", color: "bg-cyan-100 text-cyan-700" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700" },
};

const URGENCY_CONFIG = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-red-100 text-red-700",
};

function CollapsibleSection({ title, expanded, onToggle, children, testId }) {
  return (
    <div className="border rounded-sm" data-testid={testId}>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{title}</span>
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {expanded && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function SummaryField({ label, value, full }) {
  if (!value) return null;
  return (
    <div className={full ? "col-span-2" : ""}>
      <p className="text-[10px] font-mono uppercase text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm text-slate-700 dark:text-slate-300">{value}</p>
    </div>
  );
}

function SubmitReviewDialog({ open, onOpenChange, noticeId, allUsers, onSubmitted }) {
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [extName, setExtName] = useState("");
  const [extEmail, setExtEmail] = useState("");
  const [externalReviewers, setExternalReviewers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const addExternalReviewer = () => {
    if (!extName.trim() || !extEmail.trim()) { toast.error("Name and email are required"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(extEmail)) { toast.error("Invalid email address"); return; }
    if (externalReviewers.some(r => r.email === extEmail)) { toast.error("This email is already added"); return; }
    setExternalReviewers([...externalReviewers, { name: extName.trim(), email: extEmail.trim() }]);
    setExtName(""); setExtEmail("");
  };

  const handleSubmit = async () => {
    if (!selectedReviewers.length && !externalReviewers.length) { toast.error("Select at least one reviewer"); return; }
    setSubmitting(true);
    try {
      await apiClient.post(`/notices/${noticeId}/submit-review`, {
        reviewers: selectedReviewers.map(id => ({ user_id: id, role: "reviewer" })),
        external_reviewers: externalReviewers,
      });
      toast.success("Notice submitted for review");
      onSubmitted();
    } catch (err) { toast.error(err.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="submit-review-desc">
        <DialogHeader>
          <DialogTitle>Submit for Review</DialogTitle>
          <DialogDescription id="submit-review-desc">Select internal or external reviewers.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <div>
            <Label>Internal Reviewers</Label>
            <div className="max-h-40 overflow-auto border rounded-sm p-2 mt-1 space-y-1">
              {(allUsers || []).map(u => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                  <input type="checkbox" checked={selectedReviewers.includes(u.id)} onChange={e => setSelectedReviewers(prev => e.target.checked ? [...prev, u.id] : prev.filter(x => x !== u.id))} className="rounded" />
                  <span>{u.name}</span>
                  <span className="text-xs text-slate-400 ml-auto">{u.role}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-2"><Mail size={14} className="text-violet-600" /> External Reviewers</Label>
            <div className="flex gap-2 mt-2">
              <Input placeholder="Name" value={extName} onChange={e => setExtName(e.target.value)} className="rounded-sm flex-1 h-8 text-sm" data-testid="notice-ext-reviewer-name" />
              <Input placeholder="Email" type="email" value={extEmail} onChange={e => setExtEmail(e.target.value)} className="rounded-sm flex-1 h-8 text-sm" data-testid="notice-ext-reviewer-email" />
              <Button type="button" variant="outline" size="sm" onClick={addExternalReviewer} className="rounded-sm h-8 text-xs" data-testid="notice-add-ext-reviewer-btn"><Plus size={12} className="mr-1" /> Add</Button>
            </div>
            {externalReviewers.length > 0 && (
              <div className="space-y-1 mt-2">
                {externalReviewers.map((er, idx) => (
                  <div key={er.email} className="flex items-center justify-between p-1.5 border rounded-sm bg-violet-50/50 text-xs" data-testid={`notice-ext-reviewer-${idx}`}>
                    <div className="flex items-center gap-2"><Mail size={10} className="text-violet-500" /><span className="font-medium">{er.name}</span><span className="text-slate-400">{er.email}</span></div>
                    <Button variant="ghost" size="sm" className="h-5 text-[10px] text-red-500 px-1" onClick={() => setExternalReviewers(externalReviewers.filter((_, i) => i !== idx))}>Remove</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || (!selectedReviewers.length && !externalReviewers.length)} className="gap-2 rounded-sm" data-testid="confirm-submit-review-btn">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NoticeDetailDialog({ notice: initialNotice, onClose, onRefresh, allUsers, currentUser, contract }) {
  const [notice, setNotice] = useState(initialNotice);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [reviewComment, setReviewComment] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState(contract?.contracting_party?.contact_email || "");
  const [expandedSections, setExpandedSections] = useState({ summary: true, body: true, clauses: false });
  const [extFeedbackForm, setExtFeedbackForm] = useState(null);
  const [extFeedbackText, setExtFeedbackText] = useState("");
  const [extFeedbackAction, setExtFeedbackAction] = useState("approve");

  const refreshNotice = async () => {
    try { const res = await apiClient.get(`/notices/${notice.id}`); setNotice(res.data); } catch { /* ok */ }
  };

  const isCreator = notice.created_by === currentUser?.id;
  const isReviewer = notice.reviewers?.some(r => r.user_id === currentUser?.id && r.status === "pending");
  const toggle = (key) => setExpandedSections(p => ({ ...p, [key]: !p[key] }));

  const handleSave = async () => {
    setSaving(true);
    try { await apiClient.put(`/notices/${notice.id}`, editData); toast.success("Notice updated"); setEditing(false); await refreshNotice(); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this notice?")) return;
    try { await apiClient.delete(`/notices/${notice.id}`); toast.success("Notice deleted"); onClose(); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const handleReview = async (action) => {
    try { await apiClient.post(`/notices/${notice.id}/review`, { action, comments: reviewComment }); toast.success(`Notice ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "changes requested"}`); setReviewComment(""); await refreshNotice(); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Review failed"); }
  };

  const handleRecordExtFeedback = async (reviewerId) => {
    if (!extFeedbackText.trim()) return;
    try { await apiClient.post(`/notices/${notice.id}/external-review/${reviewerId}`, { action: extFeedbackAction, feedback: extFeedbackText }); toast.success("External feedback recorded"); setExtFeedbackForm(null); setExtFeedbackText(""); await refreshNotice(); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleSend = async () => {
    try { await apiClient.post(`/notices/${notice.id}/send`, { recipient_email: recipientEmail }); toast.success("Notice sent"); setSendOpen(false); await refreshNotice(); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
  };

  const sc = STATUS_CONFIG[notice.status] || STATUS_CONFIG.draft;
  const summary = notice.executive_summary;

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0" aria-describedby="notice-detail-desc">
        <DialogHeader className="px-6 pt-5 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-base">{notice.subject || `${notice.notice_type} Notice`}</DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5" id="notice-detail-desc">
                {notice.contract_title} &middot; {notice.supplier_name} &middot; {NOTICE_TYPES.find(t => t.value === notice.notice_type)?.label}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`rounded-sm text-xs ${sc.color}`} data-testid="notice-status-badge">{sc.label}</Badge>
              {summary?.urgency_level && <Badge className={`rounded-sm text-xs font-bold ${URGENCY_CONFIG[summary.urgency_level]}`} data-testid="notice-urgency-badge">{summary.urgency_level.toUpperCase()}</Badge>}
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="h-[55vh]">
          <div className="px-6 py-4 space-y-4">
            {summary && (
              <CollapsibleSection title="Executive Summary" expanded={expandedSections.summary} onToggle={() => toggle("summary")} testId="notice-section-summary">
                <div className="grid grid-cols-2 gap-3">
                  <SummaryField label="Nature of Issue" value={summary.nature_of_issue} />
                  <SummaryField label="Contractual Context" value={summary.contractual_context} />
                  <SummaryField label="Business Impact" value={summary.business_impact} />
                  <SummaryField label="Recommended Action" value={summary.recommended_action} />
                  <SummaryField label="Referenced Clauses (Simplified)" value={summary.referenced_clauses_simplified} full />
                </div>
              </CollapsibleSection>
            )}

            <CollapsibleSection title="Full Notice" expanded={expandedSections.body} onToggle={() => toggle("body")} testId="notice-section-body">
              {editing ? (
                <div className="space-y-3">
                  <div><Label className="text-xs">Subject</Label><Input value={editData.subject ?? notice.subject} onChange={e => setEditData(p => ({ ...p, subject: e.target.value }))} className="rounded-sm" /></div>
                  <div><Label className="text-xs">Required Action</Label><Input value={editData.required_action ?? notice.required_action} onChange={e => setEditData(p => ({ ...p, required_action: e.target.value }))} className="rounded-sm" /></div>
                  <div><Label className="text-xs">Timeline</Label><Input value={editData.timeline ?? notice.timeline} onChange={e => setEditData(p => ({ ...p, timeline: e.target.value }))} className="rounded-sm" /></div>
                  <div><Label className="text-xs">Notice Body</Label><Textarea value={editData.body ?? notice.body} onChange={e => setEditData(p => ({ ...p, body: e.target.value }))} rows={10} className="rounded-sm font-mono text-xs" /></div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5 rounded-sm">{saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Save</Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditData({}); }} className="rounded-sm">Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {notice.required_action && <div className="p-2 border border-amber-200 bg-amber-50/50 rounded-sm"><p className="text-[10px] font-mono uppercase text-amber-600">Required Action</p><p className="text-sm">{notice.required_action}</p></div>}
                  {notice.timeline && <div className="p-2 border border-slate-200 bg-slate-50/50 rounded-sm"><p className="text-[10px] font-mono uppercase text-slate-500">Timeline</p><p className="text-sm">{notice.timeline}</p></div>}
                  <pre className="text-sm whitespace-pre-wrap font-sans text-slate-700 dark:text-slate-300 leading-relaxed">{notice.body}</pre>
                </div>
              )}
            </CollapsibleSection>

            {notice.referenced_clauses?.length > 0 && (
              <CollapsibleSection title={`Referenced Clauses (${notice.referenced_clauses.length})`} expanded={expandedSections.clauses} onToggle={() => toggle("clauses")} testId="notice-section-clauses">
                <div className="space-y-2">
                  {notice.referenced_clauses.map((c, i) => (
                    <div key={c.clause_text || i} className="p-2 border rounded-sm" data-testid={`clause-ref-${i}`}>
                      <p className="text-xs font-mono text-slate-500 italic">"{c.clause_text}"</p>
                      {c.relevance && <p className="text-[10px] text-slate-400 mt-1">Relevance: {c.relevance}</p>}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {notice.reviewers?.length > 0 && (
              <div className="border rounded-sm p-3" data-testid="notice-reviewers">
                <p className="text-xs font-mono uppercase text-slate-500 mb-2">Internal Reviewers</p>
                <div className="space-y-1.5">
                  {notice.reviewers.map((r, i) => (
                    <div key={r.user_id || i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2"><Users size={14} className="text-slate-400" /><span>{r.user_name}</span><Badge className="rounded-sm text-[9px] bg-slate-100 text-slate-500">{r.role}</Badge></div>
                      <div className="flex items-center gap-2">
                        <Badge className={`rounded-sm text-[10px] ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "rejected" || r.status === "changes_requested" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{r.status}</Badge>
                        {r.comments && <span className="text-xs text-slate-400 max-w-[200px] truncate" title={r.comments}><MessageSquare size={10} className="inline mr-0.5" />{r.comments}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {notice.external_reviewers?.length > 0 && (
              <div className="border border-violet-200 rounded-sm p-3" data-testid="notice-external-reviewers">
                <p className="text-xs font-mono uppercase text-violet-600 mb-2 flex items-center gap-1.5"><Mail size={12} /> External Reviewers</p>
                <div className="space-y-2">
                  {notice.external_reviewers.map((er) => (
                    <div key={er.id} className="text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2"><Mail size={12} className="text-violet-400" /><span className="font-medium">{er.name}</span><span className="text-xs text-slate-400">{er.email}</span></div>
                        <Badge className={`rounded-sm text-[10px] ${er.status === "feedback_received" ? (er.feedback_action === "approve" ? "bg-emerald-100 text-emerald-700" : er.feedback_action === "reject" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700") : "bg-slate-100 text-slate-500"}`}>{er.status === "feedback_received" ? (er.feedback_action === "request_changes" ? "Changes Requested" : er.feedback_action) : "Awaiting response"}</Badge>
                      </div>
                      {er.feedback && <p className="text-xs text-slate-500 italic mt-1 ml-6 flex items-start gap-1"><MessageSquare size={10} className="mt-0.5 flex-shrink-0" />{er.feedback}</p>}
                      {er.status === "pending" && isCreator && extFeedbackForm !== er.id && <Button variant="outline" size="sm" className="mt-1.5 ml-6 h-6 text-[11px] rounded-sm gap-1" onClick={() => setExtFeedbackForm(er.id)} data-testid={`notice-record-ext-btn-${er.id}`}><MessageSquare size={10} /> Record Feedback</Button>}
                      {extFeedbackForm === er.id && (
                        <div className="mt-2 ml-6 p-2.5 border rounded-sm bg-slate-50/70 space-y-2" data-testid={`notice-ext-form-${er.id}`}>
                          <select value={extFeedbackAction} onChange={e => setExtFeedbackAction(e.target.value)} className="h-7 text-xs rounded-sm border px-2 w-40" data-testid="notice-ext-action">
                            <option value="approve">Approve</option><option value="reject">Reject</option><option value="request_changes">Request Changes</option>
                          </select>
                          <Textarea placeholder="Paste or summarize feedback..." value={extFeedbackText} onChange={e => setExtFeedbackText(e.target.value)} rows={2} className="rounded-sm text-xs" data-testid="notice-ext-feedback-text" />
                          <div className="flex gap-2">
                            <Button size="sm" className="h-6 text-[11px] rounded-sm gap-1" onClick={() => handleRecordExtFeedback(er.id)} disabled={!extFeedbackText.trim()} data-testid="notice-ext-submit-btn"><CheckCircle size={10} /> Save</Button>
                            <Button variant="outline" size="sm" className="h-6 text-[11px] rounded-sm" onClick={() => { setExtFeedbackForm(null); setExtFeedbackText(""); }}>Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isReviewer && (
              <div className="border-2 border-indigo-200 bg-indigo-50/30 rounded-sm p-4" data-testid="reviewer-action-area">
                <p className="text-xs font-mono uppercase text-indigo-600 mb-2">Your Review</p>
                <Textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Add comments (optional)" rows={2} className="rounded-sm mb-3" data-testid="review-comment-input" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleReview("approve")} className="gap-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="review-approve-btn"><CheckCircle size={14} /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => handleReview("request_changes")} className="gap-1.5 rounded-sm text-amber-600 border-amber-200" data-testid="review-changes-btn"><RefreshCw size={14} /> Request Changes</Button>
                  <Button size="sm" variant="outline" onClick={() => handleReview("reject")} className="gap-1.5 rounded-sm text-red-600 border-red-200" data-testid="review-reject-btn"><XCircle size={14} /> Reject</Button>
                </div>
              </div>
            )}

            {notice.status === "sent" && <div className="p-3 border border-cyan-200 bg-cyan-50/50 rounded-sm" data-testid="sent-info"><p className="text-xs font-mono uppercase text-cyan-600">Sent</p><p className="text-sm">To: {notice.sent_to} &middot; {new Date(notice.sent_at).toLocaleString()}</p></div>}

            {(notice.status === "sent" || notice.status === "approved") && (
              <div className="pt-2"><ExtractObligationsButton eventType="notice" eventId={notice.id} contractId={notice.contract_id} label="AI Extract Obligations" className="h-7 text-xs" /></div>
            )}
          </div>
        </ScrollArea>

        <div className="px-6 py-4 border-t flex items-center justify-between">
          <div className="flex gap-2">
            {isCreator && notice.status === "draft" && <Button size="sm" variant="ghost" onClick={handleDelete} className="gap-1.5 rounded-sm text-red-500 hover:text-red-600" data-testid="delete-notice-btn"><Trash2 size={14} /> Delete</Button>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Close</Button>
            {isCreator && (notice.status === "draft" || notice.status === "rejected") && !editing && <Button variant="outline" onClick={() => { setEditing(true); setEditData({}); }} className="gap-1.5 rounded-sm" data-testid="edit-notice-btn"><FileText size={14} /> Edit</Button>}
            {isCreator && (notice.status === "draft" || notice.status === "rejected") && <Button onClick={() => setSubmitOpen(true)} className="gap-1.5 rounded-sm" data-testid="submit-review-btn"><Send size={14} /> Submit for Review</Button>}
            {isCreator && notice.status === "approved" && <Button onClick={() => setSendOpen(true)} className="gap-1.5 rounded-sm bg-cyan-600 hover:bg-cyan-700" data-testid="send-notice-btn"><Mail size={14} /> Send Notice</Button>}
          </div>
        </div>

        <SubmitReviewDialog open={submitOpen} onOpenChange={setSubmitOpen} noticeId={notice.id} allUsers={allUsers} onSubmitted={() => { refreshNotice(); onRefresh(); setSubmitOpen(false); }} />

        {sendOpen && (
          <Dialog open={sendOpen} onOpenChange={setSendOpen}>
            <DialogContent aria-describedby="send-notice-desc">
              <DialogHeader><DialogTitle>Send Notice</DialogTitle><DialogDescription id="send-notice-desc">Send the approved notice to the supplier.</DialogDescription></DialogHeader>
              <div className="py-2 space-y-3"><div><Label>Recipient Email</Label><Input value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="supplier@example.com" className="rounded-sm" data-testid="recipient-email-input" /></div></div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSendOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={handleSend} className="gap-2 rounded-sm bg-cyan-600 hover:bg-cyan-700" data-testid="confirm-send-btn"><Mail size={16} /> Send</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
